import {
  getQuestionnaireDraftApi,
  updateQuestionnaireDraftApi,
} from "../api/questionnaireDraftsApi";
import { isQuestionnaireDraftConflict } from "./compileErrorMessage";

type QuestionnaireDraftResponse = Awaited<
  ReturnType<typeof getQuestionnaireDraftApi>
>;

/**
 * `saved`: the write was accepted. `conflict`: the stored draft had moved past
 * `expectedRevision`; `latest` is the draft the server holds now (absent if
 * that read failed), for the caller to adopt so its next write starts from
 * the current revision. Every other failure throws.
 */
export type PatchQuestionnaireDraftResult =
  | { kind: "saved"; draft: QuestionnaireDraftResponse }
  | {
      kind: "conflict";
      error: unknown;
      latest: QuestionnaireDraftResponse | undefined;
    };

/**
 * PATCH a questionnaire draft with optional compare-and-swap.
 *
 * A 409 means the stored draft moved past `expectedRevision`. Read the current
 * draft and hand it back — never re-PATCH the identical payload. That retry
 * would overwrite the write that just won.
 */
export async function patchQuestionnaireDraft(
  draftId: string,
  payload: Record<string, unknown>,
  expectedRevision?: number,
): Promise<PatchQuestionnaireDraftResult> {
  try {
    const draft = await updateQuestionnaireDraftApi(draftId, {
      ...payload,
      ...(typeof expectedRevision === "number" ? { expectedRevision } : {}),
    });
    return { kind: "saved", draft };
  } catch (error) {
    if (!isQuestionnaireDraftConflict(error)) throw error;
    const latest = await getQuestionnaireDraftApi(draftId).catch(
      () => undefined,
    );
    return { kind: "conflict", error, latest };
  }
}
